import { TYPE_BILL_ITEM } from '../constants/types';
import { safeLocaleCompare } from './sort';

/**
 * Named bill item types for dropdowns (excludes the reverse numeric enum keys).
 *
 * Kept in enum-declaration order so callers can index the array by the enum
 * value (e.g. `TYPES_BILL_ITEM[item.type][0]` to read a type's key). Do NOT
 * sort this array — reordering it breaks those positional lookups and makes the
 * billing table render the wrong type label for existing rows.
 */
export function getBillItemTypeOptions(): [string, TYPE_BILL_ITEM][] {
    return Object.entries(TYPE_BILL_ITEM)
        .filter(([key]) => isNaN(Number(key))) as [string, TYPE_BILL_ITEM][];
}

/**
 * SHER-447: the "Type" dropdown must list options alphabetically by their
 * displayed (translated) label, not by the internal enum key. Sort a copy at
 * the render site where the translate fn is available, leaving the source array
 * (and its positional lookups) untouched.
 */
export function orderBillItemTypeOptionsByLabel(
    entries: [string, TYPE_BILL_ITEM][],
    translate: (id: string) => string,
): [string, TYPE_BILL_ITEM][] {
    const labelOf = ([key]: [string, TYPE_BILL_ITEM]) =>
        String(translate(`hospital.billing.item.types.${key.toLowerCase()}`) ?? '');
    return [...entries].sort((a, b) => safeLocaleCompare(labelOf(a), labelOf(b)));
}

/**
 * SHER-546: the one place that answers "is this a usable bill-item type, and what is
 * its key?" — by asking the array the render site actually indexes, so the answer cannot
 * drift from the lookup it is protecting.
 *
 * Takes `unknown` on purpose. `BillItem.type` is declared `number`, but the add-row seeds
 * it to `""` and the Type Select stores a numeric string, so a `number` parameter would
 * assert the very invariant this helper exists to disbelieve. Returns null for anything
 * that is not a real, in-range enum position.
 */
export function getBillItemTypeKey(type: unknown): string | null {
    if (type === undefined || type === null || type === '') {
        return null;
    }
    const numeric = Number(type);
    if (!Number.isInteger(numeric)) {
        return null;
    }
    const entry = getBillItemTypeOptions()[numeric];
    return entry ? entry[0] : null;
}
